/**
 * Compare Page - Multi-site comparison view
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5
 */

import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { loadSites } from '../store/slices/sitesSlice';
import { SiteDetail, fetchSiteDetail } from '../services/api';
import ComparisonColumn from '../components/ComparisonColumn';

const MAX_SITES = 4;

function Compare() {
  const dispatch = useDispatch<AppDispatch>();
  const { sites, loading: sitesLoading, error: sitesError } = useSelector(
    (state: RootState) => state.sites
  );
  
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  const [siteDetails, setSiteDetails] = useState<Map<string, SiteDetail>>(new Map());
  const [loadingDetails, setLoadingDetails] = useState<Set<string>>(new Set());

  // Load sites on mount
  useEffect(() => {
    if (sites.length === 0) {
      dispatch(loadSites());
    }
  }, [dispatch, sites.length]);

  // Load site details when selection changes
  useEffect(() => {
    const loadDetails = async () => {
      for (const siteId of selectedSiteIds) {
        if (!siteDetails.has(siteId) && !loadingDetails.has(siteId)) {
          setLoadingDetails((prev) => new Set(prev).add(siteId));
          try {
            const detail = await fetchSiteDetail(siteId);
            setSiteDetails((prev) => new Map(prev).set(siteId, detail));
          } catch (err) {
            console.error(`Failed to load details for site ${siteId}:`, err);
          } finally {
            setLoadingDetails((prev) => {
              const next = new Set(prev);
              next.delete(siteId);
              return next;
            });
          }
        }
      }
    };
    loadDetails();
  }, [selectedSiteIds, siteDetails, loadingDetails]);

  const handleSiteToggle = (siteId: string) => {
    setSelectedSiteIds((prev) => {
      if (prev.includes(siteId)) {
        return prev.filter((id) => id !== siteId);
      }
      if (prev.length >= MAX_SITES) {
        return prev; // Don't add more than MAX_SITES
      }
      return [...prev, siteId];
    });
  };

  const handleClearSelection = () => {
    setSelectedSiteIds([]);
  };

  // Calculate comparison highlights
  const getComparisonHighlights = () => {
    const selectedDetails = selectedSiteIds
      .map((id) => siteDetails.get(id))
      .filter((d): d is SiteDetail => d !== undefined);

    if (selectedDetails.length < 2) {
      return { lowestUtilization: null, lowestCostPerCase: null };
    }

    // Find site with lowest utilization (most opportunity)
    let lowestUtilization: string | null = null;
    let minUtilization = Infinity;

    // Find site with lowest cost per case (most cost-effective)
    let lowestCostPerCase: string | null = null;
    let minCostPerCase = Infinity;

    for (const detail of selectedDetails) {
      // Calculate overall utilization
      const totalPlanned = detail.capacity.reduce((sum, row) => sum + row.plannedCases, 0);
      const totalCapacity = detail.capacity.reduce(
        (sum, row) => sum + (row.loadCapacity || row.plannedCases),
        0
      );
      const utilization = totalCapacity > 0 ? totalPlanned / totalCapacity : 0;

      if (utilization < minUtilization) {
        minUtilization = utilization;
        lowestUtilization = detail.site.siteId;
      }

      // Calculate cost per case
      const costPerCase =
        totalPlanned > 0 ? detail.site.costParameters.fixedCost / totalPlanned : Infinity;

      if (costPerCase < minCostPerCase) {
        minCostPerCase = costPerCase;
        lowestCostPerCase = detail.site.siteId;
      }
    }

    return { lowestUtilization, lowestCostPerCase };
  };

  const highlights = getComparisonHighlights();

  if (sitesLoading && sites.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading sites...</div>
      </div>
    );
  }

  if (sitesError) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>Error: {sitesError}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Site Comparison</h1>
        <p style={styles.subtitle}>
          Select up to {MAX_SITES} sites to compare side-by-side
        </p>
      </header>

      {/* Site Selector */}
      <div style={styles.selectorSection}>
        <div style={styles.selectorHeader}>
          <span style={styles.selectorLabel}>
            Selected: {selectedSiteIds.length} / {MAX_SITES}
          </span>
          {selectedSiteIds.length > 0 && (
            <button style={styles.clearButton} onClick={handleClearSelection}>
              Clear Selection
            </button>
          )}
        </div>
        <div style={styles.siteCheckboxes}>
          {sites.map((site) => (
            <label
              key={site.siteId}
              style={{
                ...styles.checkboxLabel,
                opacity:
                  selectedSiteIds.length >= MAX_SITES && !selectedSiteIds.includes(site.siteId)
                    ? 0.5
                    : 1,
              }}
            >
              <input
                type="checkbox"
                checked={selectedSiteIds.includes(site.siteId)}
                onChange={() => handleSiteToggle(site.siteId)}
                disabled={
                  selectedSiteIds.length >= MAX_SITES && !selectedSiteIds.includes(site.siteId)
                }
                style={styles.checkbox}
              />
              <span style={styles.siteName}>{site.name}</span>
              <span style={styles.siteUtilization}>
                ({(site.totalUtilization * 100).toFixed(0)}% utilized)
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Comparison View */}
      {selectedSiteIds.length === 0 ? (
        <div style={styles.emptyState}>
          <p>Select sites above to begin comparison</p>
        </div>
      ) : (
        <div style={styles.comparisonContainer}>
          <div
            style={{
              ...styles.columnsWrapper,
              gridTemplateColumns: `repeat(${selectedSiteIds.length}, 1fr)`,
            }}
          >
            {selectedSiteIds.map((siteId) => {
              const detail = siteDetails.get(siteId);
              const siteSummary = sites.find((s) => s.siteId === siteId);
              const isLoading = loadingDetails.has(siteId);

              return (
                <ComparisonColumn
                  key={siteId}
                  siteId={siteId}
                  siteName={siteSummary?.name || siteId}
                  siteDetail={detail || null}
                  isLoading={isLoading}
                  isMostOpportunity={highlights.lowestUtilization === siteId}
                  isMostCostEffective={highlights.lowestCostPerCase === siteId}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Legend */}
      {selectedSiteIds.length >= 2 && (
        <div style={styles.legend}>
          <div style={styles.legendItem}>
            <span style={{ ...styles.legendBadge, backgroundColor: '#28a745' }}>
              Most Opportunity
            </span>
            <span style={styles.legendText}>Lowest utilization - most room for growth</span>
          </div>
          <div style={styles.legendItem}>
            <span style={{ ...styles.legendBadge, backgroundColor: '#007bff' }}>
              Most Cost-Effective
            </span>
            <span style={styles.legendText}>Lowest cost per case</span>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '1.5rem',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '1.5rem',
  },
  title: {
    margin: 0,
    fontSize: '1.75rem',
    color: '#333',
  },
  subtitle: {
    margin: '0.5rem 0 0 0',
    color: '#666',
    fontSize: '0.95rem',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#666',
  },
  error: {
    textAlign: 'center',
    padding: '3rem',
    color: '#dc3545',
  },
  selectorSection: {
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1.5rem',
  },
  selectorHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
  },
  selectorLabel: {
    fontWeight: 600,
    color: '#333',
  },
  clearButton: {
    padding: '0.25rem 0.75rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  siteCheckboxes: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: 'white',
    borderRadius: '4px',
    border: '1px solid #dee2e6',
    cursor: 'pointer',
  },
  checkbox: {
    cursor: 'pointer',
  },
  siteName: {
    fontWeight: 500,
    color: '#333',
  },
  siteUtilization: {
    fontSize: '0.85rem',
    color: '#666',
  },
  emptyState: {
    textAlign: 'center',
    padding: '4rem 2rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    color: '#666',
  },
  comparisonContainer: {
    marginBottom: '1.5rem',
  },
  columnsWrapper: {
    display: 'grid',
    gap: '1rem',
  },
  legend: {
    display: 'flex',
    gap: '2rem',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    justifyContent: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  legendBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    color: 'white',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  legendText: {
    fontSize: '0.85rem',
    color: '#666',
  },
};

export default Compare;
